import { NextSeo } from "next-seo";
import { PageWrapper, SidebarLayout } from "../components/SidebarLayout";
import { Navbar } from "../components/Navbar/component";
import { UserFetcher } from "../modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { Routes } from "~/routes";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { MediaCitation } from "~/modules/media_citations/types";
import { MediaCitationsListPage } from "~/modules/media_citations/pages/list";
import { MediaCitationsSEO } from "~/modules/media_citations/seo/MediaCitationsSEO";
import { PageHeader } from "~/components/PageHeader";
import { Anchor } from "~/components/Anchor";
import { queryMediaCitations } from "~/modules/media_citations/api/router";

// // // //

const SEO_TITLE = "Press & Citations";
const SEO_DESCRIPTION =
    "Explore articles, research reports, and industry publications that cite RWA.xyz data and market insights.";

// // // //

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    const citations = await queryMediaCitations();

    return {
        props: {
            citations: citations,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export default function Citations(props: { citations: MediaCitation[] }) {
    const { citations } = props;

    return (
        <PageTrack>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.citations)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.citations),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "RWA.xyz citations, media coverage, press mentions, tokenization research, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.citations}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.citations,
                    },
                ]}
            />
            <MediaCitationsSEO
                citations={citations}
                name={SEO_TITLE}
                description={SEO_DESCRIPTION}
            />
            <UserFetcher>{({ user }) => <Navbar user={user} />}</UserFetcher>
            <SidebarLayout pathname={Routes.citations} showFooter>
                <PageWrapper>
                    <PageHeader
                        title={SEO_TITLE}
                        subtitle={
                            <>
                                Every published reference to RWA.xyz data,
                                searchable. Writing a report or story on
                                real-world asset tokenization? Contact{" "}
                                <Anchor
                                    href="mailto:team@rwa.xyz"
                                    variant="default"
                                    analyticsLabel="Citations Contact Email"
                                >
                                    team@rwa.xyz
                                </Anchor>{" "}
                                for data or assistance.
                            </>
                        }
                    />
                    <MediaCitationsListPage citations={citations} />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
